import type {
  InternalTrainers,
  ItemTimeslots,
  SendTrainerNotificationDto,
  SessionNotificationDetail,
} from "@simbelapp/order-sdk";
import { EvenStatusEnum } from "~/utils/enums/event-status.enum";
import { TrainerNotificationErrorEnum } from "~/utils/enums/session-notifications.enums";
import type { ITrainerNotificationInput } from "~/utils/interfaces/session-notifications-interfaces";
import {
  PARTNER_NOTIFICATION_SEND_FAILED,
  partnerNotificationErrorMessage,
  requestErrorMessage,
} from "~/utils/helpers/session-notifications/partner-notification";

/** Unlike a partner send, a trainer send may have recorded part of its mails before failing. */
export const TRAINER_NOTIFICATION_SEND_FAILED =
  "L'envoi a échoué. Consultez le tableau des notifications avant de réessayer.";

const ERROR_MESSAGES: Record<TrainerNotificationErrorEnum, string> = {
  [TrainerNotificationErrorEnum.NO_TRAINER_SELECTED]: "Sélectionnez au moins un formateur.",
  [TrainerNotificationErrorEnum.TRAINER_NOT_ASSIGNED]:
    "Un des formateurs n'anime plus d'étape de cette session : fermez le formulaire puis rouvrez-le.",
  [TrainerNotificationErrorEnum.TRAINER_WITHOUT_EMAIL]:
    "Un des formateurs n'a pas d'adresse e-mail : retirez-le des destinataires.",
};

const FORBIDDEN_MESSAGE = "Vous n'avez pas les droits pour notifier les formateurs.";
const SUBJECT_TOO_LONG_MESSAGE = "Le sujet du mail est trop long : raccourcissez-le.";

/** class-validator constraints orders answers a malformed trainer send with. */
const EMPTY_SELECTION_CONSTRAINT = "trainerUserIds should not be empty";
const MAX_LENGTH_CONSTRAINT = "must be shorter than or equal to";

/** Each trainer of the session's steps once, in step order: who a trainer send can reach. */
export function sessionTrainers(
  timeslots: Pick<ItemTimeslots, "internalTrainers">[] | null | undefined,
): InternalTrainers[] {
  const trainers = new Map<string, InternalTrainers>();
  for (const trainer of (timeslots ?? []).flatMap((timeslot) => timeslot.internalTrainers ?? [])) {
    if (!trainers.has(trainer.userId)) trainers.set(trainer.userId, trainer);
  }
  return [...trainers.values()];
}

/** The orders payload of a trainer send: the selection is always explicit, never « every trainer ». */
export function buildTrainerNotificationPayload(input: ITrainerNotificationInput): SendTrainerNotificationDto {
  return {
    trainerUserIds: [...new Set(input.trainerUserIds)],
    subject: input.subject.trim(),
    content: input.content ?? "",
    ...(input.templateId ? { templateId: input.templateId } : {}),
    documentIds: [...new Set(input.documentIds)],
    sendIcs: input.sendIcs,
    sendUrlSession: input.sendUrlSession,
  };
}

/**
 * French copy of a refused trainer send, read from the message orders answers with. What a trainer
 * send shares with a partner send (subject, content, documents, template) reads the same.
 */
export function trainerNotificationErrorMessage(message: unknown): string {
  const raw = Array.isArray(message) ? message.map(String).join("\n") : typeof message === "string" ? message : "";

  const known = Object.values(TrainerNotificationErrorEnum).find((value) => raw.includes(value));
  if (known) return ERROR_MESSAGES[known];
  if (raw.includes(EMPTY_SELECTION_CONSTRAINT)) return ERROR_MESSAGES[TrainerNotificationErrorEnum.NO_TRAINER_SELECTED];
  if (raw.includes(MAX_LENGTH_CONSTRAINT)) return SUBJECT_TOO_LONG_MESSAGE;
  if (/forbidden|unauthorized/i.test(raw)) return FORBIDDEN_MESSAGE;

  const shared = partnerNotificationErrorMessage(message);
  return shared === PARTNER_NOTIFICATION_SEND_FAILED ? TRAINER_NOTIFICATION_SEND_FAILED : shared;
}

/** French copy of a rejected trainer send, whether it comes as a bare message or a `useSDKApi` rejection. */
export async function trainerNotificationRequestError(error: unknown): Promise<string> {
  return trainerNotificationErrorMessage(await requestErrorMessage(error));
}

/**
 * The toast once orders recorded a trainer send. Orders answers 201 with the mails still queued
 * (`to_send`) in its outbox, so only a delivery it already settled unsent is a failure. With the
 * invitation, a trainer gets one delivery per step they lead.
 */
export function trainerSendFeedback(detail: Pick<SessionNotificationDetail, "deliveries">): {
  success: boolean;
  message: string;
} {
  const deliveries = detail.deliveries ?? [];
  const recipients = new Set(deliveries.map((delivery) => delivery.email));
  const undelivered = [
    ...new Set(
      deliveries
        .filter((delivery) => delivery.status !== EvenStatusEnum.SENT && delivery.status !== EvenStatusEnum.TO_SEND)
        .map((delivery) => delivery.email),
    ),
  ];

  if (!undelivered.length) {
    return {
      success: true,
      message:
        recipients.size === 1
          ? "La notification a bien été envoyée au formateur"
          : "La notification a bien été envoyée aux formateurs",
    };
  }
  if (undelivered.length === recipients.size) {
    return { success: false, message: "L'envoi aux formateurs a échoué : la notification apparaît en échec." };
  }
  return {
    success: false,
    message: `L'envoi a échoué pour ${undelivered.join(", ")} : les autres formateurs ont bien été notifiés.`,
  };
}
